'use client'

import React, { ReactNode, MouseEvent, useState } from 'react'
import { X24 } from '@vinted/monochrome-icons'
import { Button, Dialog, Divider, Flex, Image, Spacer, Text, Validation } from '@vinted/web-ui'

import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { SelectableInputDropdown } from '@marketplace-web/common-components/selectable-input-dropdown-ui'
import { useAsset } from '@marketplace-web/shared/assets'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import { toParams } from '@marketplace-web/shared/utils'
import { useCatalogContext } from '@marketplace-web/catalog/catalog-page-data'
import {
  generateAlertsFromSearchParams,
  generateAlertsFromFirstPage,
  checkIsAlertsGeneratorBusy,
  checkIsAlertsGeneratorBatchBusy,
  BrandDto,
  ReportReasonCatalogSystem,
  REPORT_REASONS_WITH_BRAND,
} from '@marketplace-web/proactive-moderation/proactive-moderation-data'

import { useProactiveModerationContext } from '../../context'
import BrandSearchDropdown from '../BrandSearchDropdown'

const MAX_SEARCH_COUNT = 500

const CATALOG_SYSTEM_REPORT_REASONS = Object.values(ReportReasonCatalogSystem).map(reason => ({
  id: reason,
  title: reason,
}))

const Title = ({
  title,
  type = Text.Type.Heading,
}: {
  title: string
  type?: React.ComponentProps<typeof Text>['type']
}) => (
  <Text
    as="h1"
    type={type}
    text={title}
    width={Text.Width.Parent}
    alignment={Text.Alignment.Center}
  />
)

const IconImage = ({ name }: { name: string }) => {
  const asset = useAsset()

  return (
    <div className="u-flexbox u-justify-content-center">
      <Image
        src={asset(`${name}.svg`, { theme: { dark: `${name}-dark.svg` } })}
        size={Image.Size.Large}
      />
    </div>
  )
}

const CloseButton = ({ text, onClose }: { onClose: () => void; text: string }) => (
  <Button text={text} onClick={onClose} styling={Button.Styling.Flat} />
)

const ModalSection = ({ children }: { children: Array<ReactNode> | ReactNode }) => {
  if (Array.isArray(children)) {
    return (
      <div style={{ padding: '16px' }}>
        <Flex justify="center" align="center" direction="column">
          <SeparatedList separator={<Spacer size={Spacer.Size.Large} />}>{children}</SeparatedList>
        </Flex>
      </div>
    )
  }

  return (
    <div style={{ padding: '16px' }}>
      <Flex>{children}</Flex>
    </div>
  )
}

const GenericModalContent = ({
  title,
  text,
  icon,
  onClose,
}: {
  title: string
  text: string
  icon?: string
  onClose: () => void
}) => (
  <>
    <ModalSection>
      {icon && <IconImage name={icon} />}
      <Title title={title} />
      <Text as="span" text={text} />
    </ModalSection>
    <ModalSection>
      <CloseButton onClose={onClose} text="Close" />
    </ModalSection>
  </>
)

type SendToModerationVariant = 'all' | 'selected'

type Props = {
  variant: SendToModerationVariant
}

const SendToModeration = ({ variant }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [modalUiState, setModalUiState] = useState(UiState.Idle)
  const [sendUiState, setSendUiState] = useState(UiState.Idle)
  const [selectedReportReason, setSelectedReportReason] = useState<ReportReasonCatalogSystem>()
  const [selectedBrand, setSelectedBrand] = useState<BrandDto>()
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false)

  const {
    state: {
      items: { pagination },
    },
  } = useCatalogContext()

  const { selectedItemIds } = useProactiveModerationContext()

  const totalEntries = pagination?.total_entries || 0
  const isCountOutOfLimits = totalEntries > MAX_SEARCH_COUNT

  const isSendAllVariant = variant === 'all'
  const isSelectedVariant = variant === 'selected'

  const checkAlertsGenerationStatus = async () => {
    setModalUiState(UiState.Pending)

    const response = isSendAllVariant
      ? await checkIsAlertsGeneratorBusy()
      : await checkIsAlertsGeneratorBatchBusy()

    if ('errors' in response || response.is_busy) {
      setModalUiState(UiState.Failure)

      return
    }

    setModalUiState(UiState.Success)
  }

  const handleOpenModal = async (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (modalUiState === UiState.Pending) {
      event.preventDefault()

      return
    }

    if (isSendAllVariant && isCountOutOfLimits) {
      setIsOpen(true)

      return
    }

    await checkAlertsGenerationStatus()

    setIsOpen(true)
  }

  const handleCloseModal = () => {
    setIsOpen(false)
    setSendUiState(UiState.Idle)
    setModalUiState(UiState.Idle)
    setSelectedReportReason(undefined)
    setSelectedBrand(undefined)
    setIsSubmitAttempted(false)
  }
  const shouldSubmitWithBrand =
    selectedReportReason && REPORT_REASONS_WITH_BRAND.includes(selectedReportReason)

  const handleSendToModeration = async (
    event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    if (sendUiState === UiState.Pending) {
      event.preventDefault()

      return
    }

    if (isSelectedVariant && !selectedItemIds) {
      setSendUiState(UiState.Failure)

      return
    }

    setIsSubmitAttempted(true)

    if (!selectedReportReason) return
    if (shouldSubmitWithBrand && !selectedBrand) return

    setSendUiState(UiState.Pending)

    const brand = shouldSubmitWithBrand ? selectedBrand : undefined

    const response = isSendAllVariant
      ? await generateAlertsFromSearchParams({
          searchParams: toParams(window.location.search),
          reportReason: selectedReportReason,
          brand,
        })
      : await generateAlertsFromFirstPage({
          itemIds: selectedItemIds!,
          reportReason: selectedReportReason,
          brand,
        })

    if ('errors' in response) {
      setSendUiState(UiState.Failure)

      return
    }

    setSendUiState(UiState.Success)
    setSelectedReportReason(undefined)
  }

  const handleReportReasonSelect = (value?: { id: string; title: string } | null) => {
    if (value) {
      setSelectedReportReason(value.id as ReportReasonCatalogSystem)
    } else {
      setSelectedReportReason(undefined)
    }

    setSelectedBrand(undefined)
    setIsSubmitAttempted(false)
  }

  const getSelectedReportReasonValue = () => {
    if (!selectedReportReason) return null

    return CATALOG_SYSTEM_REPORT_REASONS.find(reason => reason.id === selectedReportReason) || null
  }

  const renderSendReportsPrompt = () => (
    <>
      <div style={{ position: 'relative', padding: '16px 0' }}>
        <Title title="Create and send reports" type={Text.Type.Title} />
        <div
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          <Button
            styling={Button.Styling.Flat}
            onClick={handleCloseModal}
            iconName={X24}
            testId="send-to-alerts-modal-close-button"
            inline
          />
        </div>
      </div>
      <Divider />
      {isSendAllVariant ? (
        <ModalSection>
          <Text
            as="span"
            text={`You can send ${MAX_SEARCH_COUNT} alerts at once. If you want to send more, wait a couple of minutes and repeat actions.`}
          />
          <Text
            as="span"
            text="Creating and sending alerts can take some time. You won't be able to send new ones immediately"
          />
        </ModalSection>
      ) : (
        <ModalSection>
          <Text
            as="span"
            text={`You're about to send selected (${selectedItemIds?.length || 0}) items to alerts.`}
          />
          <Text
            as="span"
            text="Creating and sending alerts can take some time. You won't be able to send new ones immediately"
          />
        </ModalSection>
      )}
      <ModalSection>
        <div className="u-fill-width">
          <Text as="span" text="Report reason" type={Text.Type.Subtitle} />
          <Spacer />
          <SelectableInputDropdown
            name="report-reason"
            inputTitle=""
            textSave=""
            placeholder="Select a report reason"
            data={CATALOG_SYSTEM_REPORT_REASONS}
            selectedValue={getSelectedReportReasonValue()}
            onSave={handleReportReasonSelect}
            isPhone={false}
            validation={
              isSubmitAttempted && !selectedReportReason ? (
                <Validation text="Please select reason" theme="warning" />
              ) : undefined
            }
            styling="tight"
          />
        </div>
      </ModalSection>
      {shouldSubmitWithBrand && (
        <ModalSection>
          <BrandSearchDropdown
            selectedBrand={selectedBrand}
            setSelectedBrand={setSelectedBrand}
            isSubmitAttempted={isSubmitAttempted}
          />
        </ModalSection>
      )}
      <ModalSection>
        <Button
          text="Send"
          onClick={handleSendToModeration}
          styling={Button.Styling.Filled}
          isLoading={sendUiState === UiState.Pending}
        />
      </ModalSection>
    </>
  )

  const renderModalContent = () => {
    if (modalUiState === UiState.Failure)
      return (
        <GenericModalContent
          icon="clock"
          title="System is already generating alerts"
          text="Please wait till all alerts are generated and try again"
          onClose={handleCloseModal}
        />
      )

    if (sendUiState === UiState.Success)
      return (
        <GenericModalContent
          icon="check-circle"
          title="Listings were sent"
          text="The system has started to work on generating alerts. It will take some time to create all alerts"
          onClose={handleCloseModal}
        />
      )

    if (sendUiState === UiState.Failure)
      return (
        <GenericModalContent
          title="Listings weren't sent to alerts"
          text="An unknown error occurred while trying to send listings to alerts. Please try again"
          onClose={handleCloseModal}
        />
      )

    return renderSendReportsPrompt()
  }

  const buttonText = isSendAllVariant
    ? 'Send All Items to Moderation'
    : 'Send Selected Items to Moderation'

  return (
    <>
      <Button
        text={buttonText}
        onClick={handleOpenModal}
        size={Button.Size.Small}
        styling={Button.Styling.Filled}
        isLoading={modalUiState === UiState.Pending}
        disabled={isSelectedVariant && !selectedItemIds?.length}
        inline
      />
      <Dialog
        show={isOpen}
        defaultCallback={handleCloseModal}
        closeOnOverlay
        className="u-overflow-visible"
      >
        {renderModalContent()}
      </Dialog>
    </>
  )
}

export default SendToModeration
